import { Validators } from "@angular/forms";
import { BasespaceFormControl, BasespaceFormProperties } from "../bs-app-launch-form-control";
import { BsAppLaunchNumericControlComponent } from "./bs-app-launch-numeric-control.component";

export interface NumericFormControlProperties extends BasespaceFormProperties<BsAppLaunchNumericControlComponent> {
    min?: number;
    max?: number;
    numericType: NumericType;
}

export enum NumericType {
    INTEGER = "Integer",
    FLOAT = "FloatingPoint"
}

// Reference class: src/Cloud.FormBuilder/Models/Numeric.cs
export class NumericFormControl extends BasespaceFormControl<BsAppLaunchNumericControlComponent> implements NumericFormControlProperties {
    min?: number;
    max?: number;
    numericType: NumericType;
    overrideDefaultSize?: number;

    constructor(
        data: NumericFormControlProperties
    ) {
        const { 
            id, 
            componentReference, label, 
            helpText, 
            value, 
            required, 
            requiredMessage, 
            min, 
            max, 
            numericType, 
            size, 
            tabularFieldSetStyling,
            formControlType
        } = data;

        super({ 
            id, 
            componentReference, 
            label, 
            helpText, 
            size, 
            value, 
            required, 
            requiredMessage, 
            tabularFieldSetStyling,
            formControlType
        });

        // Default size from dev portal
        this.min = min;
        this.max = max;
        this.overrideDefaultSize = 50;

        this.numericType = numericType;
    
        var validators = [];
        if(required) {
            validators.push(Validators.required);
        }

        if (min) {
            validators.push(Validators.min(min));
        }

        if (max) {
            validators.push(Validators.max(max));
        }

        if (numericType === NumericType.INTEGER) {
            validators.push(Validators.pattern(/^-?\d+$/));
        } else if (numericType === NumericType.FLOAT) {
            validators.push(Validators.pattern(/^-?\d+(\.\d+)?$/));
        } else {
            throw new Error(`Invalid numericType: ${numericType}`);
        }

        if (validators.length > 0) {
            this.setValidators(validators);
        }

        this.setValue(value || '');
        this.setupValidationHandler();
    }

    /**
     * Sets up the validation change handler to automatically update basespaceErrors
     */
    private setupValidationHandler(): void {
        // Subscribe to status changes to update basespaceErrors
        this.subs.sink = this.statusChanges.subscribe(() => {
            this.updateBasespaceErrors();
        });

        // Also update on value changes in case validation state changes
        this.subs.sink = this.valueChanges.subscribe(() => {
            this.updateBasespaceErrors();
        });

        // Initial update
        this.updateBasespaceErrors();
    }

    /**
     * Updates basespaceErrors based on current validation state
     */
    private updateBasespaceErrors(): void {
        let errorMessage = this.getErrorMessage();
        
        if (errorMessage) {
            // Set basespaceErrors if there's a validation error
            this.basespaceErrors = [{ message: errorMessage }];
        } else {
            // Clear basespaceErrors if validation passes
            this.basespaceErrors = [];
        }
    }

    /**
     * Gets appropriate error message based on the current validation errors
     * @returns Error message string or null if no errors
     */
    getErrorMessage(): string | null {
        if (!this.errors) {
            return null;
        }

        let errorMessage: string = null;

        if (this.errors.required) {
            return this.requiredMessage || `${this.label} is required`;
        }

        if (this.errors.min && this.errors.max) {
            errorMessage = `The value must be between ${this.min} and ${this.max}`;
        } else if (this.errors.min) {
            errorMessage = `The value must be greater than or equal to ${this.min}`;
        } else if (this.errors.max) {
            errorMessage = `The value must be less than or equal to ${this.max}`;
        }
        if (this.errors.pattern) {
            if (this.numericType === NumericType.INTEGER) {
                errorMessage = errorMessage ? errorMessage + ' and must be a valid integer' : 'The value must be a valid integer';
            } else if (this.numericType === NumericType.FLOAT) {
                errorMessage = errorMessage ? errorMessage + ' and must be a valid floating point number' : 'The value must be a valid floating point number';
            }
        }

        return errorMessage ? errorMessage + '.' : null;;
    }
    
}